import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Application, ApplicationType } from 'app/models/application';
import { ApplicationsService } from 'app/services/applications.service';
import { SessionStorage } from 'app/storage/session.storage';

@Component({
  selector: 'app-my-applications',
  templateUrl: './my-applications.component.html',
  styleUrls: ['./my-applications.component.scss']
})
export class MyApplicationsComponent implements OnInit {
  loading = true;
  applications: Application[] = [];

  readonly PROJECT_TYPES: ApplicationType[] = ['service1', 'service2'];

  readonly APPLICATION_METADATA: Record<ApplicationType, { title: string; description: string }> = {
    epe: {
      title: 'Pool of Standardisation Experts',
      description: 'The EPE open call is open to experts with documented experience in standardisation and relevant technical or sectoral expertise aligned with HSbooster.eu topics. Applicants should demonstrate, through the application form, their professional background, involvement in standards development activities, knowledge of relevant SDOs, TCs or WGs, and ability to provide structured support to R&I projects.\n\nApplications will be evaluated based on completeness, relevance of expertise, standardisation experience, alignment with HSbooster.eu priorities, and suitability for service delivery.\n\nBased on their profile, selected experts may be involved not only in the Mentoring Service, but also in Accelerator Services and Proposal Support Services where their expertise matches the needs of applicants. Inclusion in the EPE does not guarantee immediate assignment; experts will be contacted when a suitable service opportunity is identified.'
    },
    service1: {
      title: 'Mentoring',
      description: 'The Mentoring Service is open to EU-funded and nationally or regionally funded research and innovation projects whose expected results are aligned with HSbooster.eu II open call topics and who have a genuine need for standardisation support. Applicants should demonstrate, through the application form, the relevance of their project to the selected topics, the support requested, and any existing knowledge of relevant SDOs, technical committees or working groups.\n\nApplications are evaluated on completeness, relevance of the project\'s standardisation needs, and alignment with HSbooster.eu II priorities. We are committed to identifying the most suitable expert for each project; matching may take some time, and applicants will be contacted as soon as a suitable mentor has been found.'
    },
    service2: {
      title: 'Proposal Support Service',
      description: 'The Proposal Support Service is open to applicants preparing a future EU-funded or nationally/regionally funded research and innovation project who want standardisation support included from the proposal stage. Applicants should demonstrate, through the application form, their funding scheme, the support requested, and any existing knowledge of relevant SDOs, technical committees or working groups.\n\nApplications are evaluated on completeness, relevance of the future project\'s standardisation needs, and alignment with HSbooster.eu II priorities. Applicants will be contacted once a suitable expert has been identified.'
    },
    service3: {
      title: 'Service 3',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Vestibulum tortor quam, feugiat vitae, ultricies eget, tempor sit amet, ante.'
    },
  };

  constructor(
    private applicationsService: ApplicationsService,
    public auth: SessionStorage,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.auth.userId) {
      this.fetch(this.auth.userId);
      return;
    }
    this.auth.auth$.subscribe(info => {
      const id = info?.id;
      if (id) {
        this.fetch(id);
      } else {
        this.loading = false;
      }
    });
  }

  private fetch(userId: number): void {
    this.applicationsService.getByUser(userId).subscribe({
      next: (apps) => { this.applications = apps; this.loading = false; },
      error: () => { this.loading = false; }
    });
  }

  getApplications(type: ApplicationType): Application[] {
    return this.applications.filter(a => a.application_type === type);
  }

  private routeForType(type: ApplicationType, id?: string): any[] {
    if (type === 'epe') return ['/application-form/epe'];
    if (type === 'service2') return id ? ['/application-form/pss', id] : ['/application-form/pss'];
    return id ? ['/application-form/service', type, id] : ['/application-form/service', type];
  }

  onApply(type: ApplicationType): void {
    this.router.navigate(this.routeForType(type));
  }

  onEdit(application: Application): void {
    const raw: any = application._id;
    const id = raw?.['$oid'] ?? String(raw);
    this.router.navigate(this.routeForType(application.application_type as ApplicationType, id));
  }

  onView(applicationId: string): void {
    this.router.navigate(['/application', applicationId]);
  }
}
